import { useListState } from '@react-stately/list'
import { FocusableElement } from '@react-types/shared'
import { ListBoxBase } from 'components/ListBox/ListBoxBase'
import { ListBoxProps } from 'components/ListBox/Types'
import { type EditorView } from 'prosemirror-view'
import { useRef, useEffect, useCallback } from 'react'
import { SuggestionsSection } from './Types'
import { ListKeyboardDelegate, useSelectableList } from '@react-aria/selection'
import { SELECT_KEYS } from './constants'

interface Props extends ListBoxProps<SuggestionsSection> {
  view: EditorView
}

/**
 * This components renders a listbox of suggestions, and attaches keyboard event
 * handlers to navigate the list and select a suggestion to the view.
 */
export const List = ({ view, items, ...props }: Props) => {
  const viewRef = useRef(view.dom as HTMLElement)
  const state = useListState({
    ...props,
    items,
    selectionMode: 'single',
  })

  const keyboardDelegate = new ListKeyboardDelegate(state.collection, state.disabledKeys, viewRef)

  const { listProps } = useSelectableList({
    selectionManager: state.selectionManager,
    collection: state.collection,
    disabledKeys: state.disabledKeys,
    ref: viewRef,
    keyboardDelegate,
    shouldFocusWrap: true,
  })

  const handleKeyDown = useCallback(
    (e: Event) => {
      if (!(e instanceof KeyboardEvent)) {
        return
      }

      listProps.onKeyDown?.(e as unknown as React.KeyboardEvent<FocusableElement>)
      if (SELECT_KEYS.includes(e.key)) {
        e.stopPropagation()
        state.selectionManager.replaceSelection(state.selectionManager.focusedKey)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items],
  )

  useEffect(() => {
    view.dom.addEventListener('keydown', handleKeyDown)
    return () => {
      view.dom.removeEventListener('keydown', handleKeyDown)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view.dom, handleKeyDown])

  useEffect(() => {
    state.selectionManager.setFocusedKey(keyboardDelegate.getFirstKey())
    return () => {
      state.selectionManager.clearSelection()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items])

  return <ListBoxBase aria-label="Suggestions" state={state} />
}
